import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, API } from "@/lib/api";
import {
  TrendingUp, BarChart3, Flame, Clock, DollarSign,
  Heart, ArrowLeft, Trophy, Sparkles, Activity, Mail, Search, Zap, X,
  Bot, Send,
} from "lucide-react";
import { toast } from "sonner";
import DigestPreviewModal from "@/components/DigestPreviewModal";
import ShareMyWeekCard from "@/components/ShareMyWeekCard";

const WINDOWS = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];
const SORTS = [
  { value: "engagement", label: "Engagement", icon: Activity },
  { value: "reactions",  label: "Reactions",  icon: Heart },
  { value: "revenue",    label: "Revenue",    icon: DollarSign },
  { value: "recent",     label: "Recent",     icon: Clock },
];

export default function CreatorAnalytics() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [sort, setSort] = useState("engagement");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);
  const [searchStats, setSearchStats] = useState(null);
  const [boostHistory, setBoostHistory] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const [r, sr, bh] = await Promise.all([
        api.get("/creator/analytics/posts", { params: { days, sort, limit: 50 } }),
        api.get("/products/me/search-analytics").catch(() => ({ data: null })),
        api.get("/products/me/boost-history").catch(() => ({ data: { entries: [] } })),
      ]);
      setData(r.data);
      setSearchStats(sr.data);
      setBoostHistory(bh.data?.entries || []);
    } catch (e) {
      console.warn("[CreatorAnalytics] load failed", e);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [days, sort]);

  const rows = data?.rows || [];
  const totals = data?.totals || { posts: 0, reactions: 0, revenue_net: 0, transactions: 0 };

  const maxEngagement = useMemo(
    () => Math.max(1, ...rows.map((r) => r.engagement_per_hour || 0)),
    [rows]
  );
  const maxReactions = useMemo(
    () => Math.max(1, ...rows.map((r) => r.reactions || 0)),
    [rows]
  );

  return (
    <div className="space-y-6" data-testid="analytics-deepdive">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <button
            onClick={() => navigate("/creator")}
            data-testid="analytics-back"
            className="text-xs text-[#64748B] hover:text-[#DD2A7B] flex items-center gap-1 mb-2"
          >
            <ArrowLeft size={12} /> Back to Creator
          </button>
          <div className="text-xs uppercase tracking-[0.25em] text-[#DD2A7B] mb-1">post analytics</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Performance deep-dive</h1>
          <p className="text-[#64748B] text-sm mt-1">See what&rsquo;s working — per post, per reaction, per dollar.</p>
        </div>
        <div className="flex items-center gap-2" data-testid="analytics-window-picker">
          <button
            onClick={() => setDigestOpen(true)}
            data-testid="open-digest-preview"
            className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all border border-[#DD2A7B] text-[#DD2A7B] bg-white hover:bg-[#DD2A7B] hover:text-white flex items-center gap-1.5 mr-1"
            title="Preview the weekly digest email you'd receive on Monday"
          >
            <Mail size={12} /> Weekly digest
          </button>
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              data-testid={`window-${w.label}`}
              onClick={() => setDays(w.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                days === w.value
                  ? "bg-[#DD2A7B] text-white border-[#DD2A7B] shadow-md shadow-[rgba(221,42,123,0.30)]"
                  : "bg-white text-[#64748B] border-slate-200 hover:border-[#DD2A7B]"
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {/* Totals row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard icon={BarChart3} label="Posts in window" value={totals.posts} color="#DD2A7B" testid="total-posts" />
        <SummaryCard icon={Heart}     label="Total reactions" value={totals.reactions} color="#0EA5E9" testid="total-reactions" />
        <SummaryCard icon={DollarSign} label="Revenue (net)" value={`$${(totals.revenue_net || 0).toFixed(2)}`} color="#F59E0B" testid="total-revenue" />
        <SummaryCard icon={Trophy}    label="Transactions" value={totals.transactions} color="#8134AF" testid="total-transactions" />
      </div>

      {/* Tweet-my-best-week share card — surfaces only when totals are non-trivial */}
      <ShareMyWeekCard totals={totals} days={days} />

      {/* Sort tabs */}
      <div className="flex flex-wrap gap-2" data-testid="analytics-sort-tabs">
        {SORTS.map((s) => {
          const Icon = s.icon;
          const active = sort === s.value;
          return (
            <button
              key={s.value}
              data-testid={`sort-${s.value}`}
              onClick={() => setSort(s.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border flex items-center gap-1.5 ${
                active
                  ? "bg-[#0F172A] text-white border-[#0F172A]"
                  : "bg-white text-[#64748B] border-slate-200 hover:border-[#0F172A] hover:text-[#0F172A]"
              }`}
            >
              <Icon size={12} /> {s.label}
            </button>
          );
        })}
      </div>

      {/* Per-post rows */}
      <div className="glass-heavy rounded-3xl overflow-hidden" data-testid="analytics-rows">
        <div className="grid grid-cols-[1fr_90px_120px_120px_110px] gap-3 px-5 py-3 border-b border-slate-200 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-semibold">
          <div>Post</div>
          <div className="text-right">Reactions</div>
          <div className="text-right">Engagement / hr</div>
          <div className="text-right">Revenue</div>
          <div className="text-right">$ / Reaction</div>
        </div>

        {busy && rows.length === 0 ? (
          <div className="p-10 text-center text-[#94A3B8] text-sm">Crunching numbers…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-[#94A3B8] text-sm">
            <Sparkles size={20} className="mx-auto mb-3 text-[#94A3B8]" />
            No posts in this window. Publish something to start seeing analytics.
          </div>
        ) : rows.map((r, i) => (
          <motion.div
            key={r.post_id}
            data-testid={`row-${r.post_id}`}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.02 }}
            className="grid grid-cols-[1fr_90px_120px_120px_110px] gap-3 px-5 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <ZoneChip zone={r.zone} mediaType={r.media_type} />
                <span className="text-[10px] text-[#94A3B8]">{r.hours_live}h live</span>
              </div>
              <Link
                to={`/social`}
                className="text-sm text-[#0F172A] hover:text-[#DD2A7B] truncate block leading-snug"
              >
                {r.content || <span className="italic text-[#94A3B8]">— no caption —</span>}
              </Link>
            </div>

            <BarValue value={r.reactions} max={maxReactions} color="#0EA5E9" />
            <BarValue value={r.engagement_per_hour} max={maxEngagement} color="#DD2A7B" suffix="/hr" decimals={2} />

            <div className="text-right">
              <div className={`font-display font-bold ${r.revenue_net > 0 ? "text-[#F59E0B]" : "text-[#94A3B8]"}`}>
                ${(r.revenue_net || 0).toFixed(2)}
              </div>
              {r.transactions > 0 && (
                <div className="text-[10px] text-[#94A3B8]">{r.transactions} txn</div>
              )}
            </div>

            <div className="text-right text-sm text-[#64748B]">
              {r.reactions > 0 && r.revenue_net > 0
                ? `$${r.revenue_per_reaction.toFixed(3)}`
                : <span className="text-[#94A3B8]">—</span>}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="text-xs text-[#94A3B8] text-center">
        <Flame size={10} className="inline -mt-0.5 mr-1" />
        Engagement = reactions ÷ hours since posted. Revenue attribution requires posts to be linked to tips/products via metadata; until then, revenue rows reflect only attributed transactions.
      </div>

      {/* AI Ops console — ask Claude "why did engagement drop?" with live analytics injected. */}
      <CreatorAIOpsConsole
        data={data}
        searchStats={searchStats}
        boostHistory={boostHistory}
        days={days}
      />

      {/* Marketplace search analytics — surfaces which queries drive clicks to the seller's listings. */}
      {searchStats && (searchStats.products?.length > 0 || searchStats.top_queries?.length > 0) && (
        <SearchAnalyticsCard stats={searchStats} />
      )}

      {/* Boost history — track which AI-rewritten titles actually moved the needle. */}
      {boostHistory.length > 0 && <BoostHistoryCard entries={boostHistory} />}

      <DigestPreviewModal open={digestOpen} onClose={() => setDigestOpen(false)} />
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div data-testid={testid} className="glass rounded-2xl p-4 flex items-start gap-3">
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: `${color}1A` }}
      >
        <Icon size={16} style={{ color }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">{label}</div>
        <div className="font-display font-bold text-xl mt-0.5" style={{ color: "#0F172A" }}>{value}</div>
      </div>
    </div>
  );
}

function ZoneChip({ zone, mediaType }) {
  const map = {
    social: { bg: "rgba(14,165,233,0.12)", fg: "#0284C7", label: "Social" },
    creator: { bg: "rgba(221,42,123,0.12)", fg: "#BE185D", label: "Creator" },
  };
  const c = map[zone] || map.social;
  const mt = mediaType && mediaType !== "post" ? ` · ${mediaType}` : "";
  return (
    <span
      className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
      style={{ background: c.bg, color: c.fg }}
    >
      {c.label}{mt}
    </span>
  );
}

function BarValue({ value, max, color, suffix = "", decimals = 0 }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="text-right">
      <div className="font-display font-bold text-sm" style={{ color: value > 0 ? color : "#94A3B8" }}>
        {decimals > 0 ? Number(value).toFixed(decimals) : Number(value).toLocaleString()}{suffix && <span className="text-[10px] text-[#94A3B8] ml-0.5">{suffix}</span>}
      </div>
      <div className="mt-1 h-1 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color, opacity: 0.65 }} />
      </div>
    </div>
  );
}


function SearchAnalyticsCard({ stats }) {
  const [openProductId, setOpenProductId] = useState(null);
  const [perProduct, setPerProduct] = useState({});
  const [boostFor, setBoostFor] = useState(null);

  const expand = async (productId) => {
    if (openProductId === productId) { setOpenProductId(null); return; }
    setOpenProductId(productId);
    if (perProduct[productId]) return;
    try {
      const r = await api.get(`/products/${productId}/search-analytics`);
      setPerProduct((s) => ({ ...s, [productId]: r.data }));
    } catch (e) {
      console.warn("[search-analytics] expand failed", e);
    }
  };

  return (
    <div className="glass-heavy rounded-2xl p-5" data-testid="seller-search-analytics">
      <div className="flex items-center gap-2 mb-4">
        <Search size={16} className="text-[#DD2A7B]" />
        <div className="font-display font-bold">Search analytics — marketplace</div>
        <div className="text-xs text-[#94A3B8]">queries → clicks → CTR. Tap a listing to see per-query CTR.</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mb-2">Top queries driving clicks</div>
          {stats.top_queries.length === 0 ? (
            <div className="text-sm text-[#94A3B8]">No clicks from search yet — your listings will appear here as buyers start searching.</div>
          ) : (
            <ul className="space-y-1.5">
              {stats.top_queries.map((q, i) => (
                <li key={`q-${i}`} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5">
                  <span className="text-[#0F172A] truncate">&ldquo;{q.q || "(empty)"}&rdquo;</span>
                  <span className="font-bold text-[#DD2A7B] tabular-nums">{q.clicks}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mb-2">Clicks per listing</div>
          <ul className="space-y-1">
            {stats.products.slice(0, 10).map((p) => (
              <li key={p.product_id} className="border-b border-slate-100">
                <button
                  onClick={() => expand(p.product_id)}
                  data-testid={`expand-listing-${p.product_id}`}
                  className="w-full flex items-center justify-between text-sm py-1.5 hover:text-[#DD2A7B] transition"
                >
                  <span className="text-[#0F172A] truncate flex-1 mr-2 text-left">{p.title || p.product_id}</span>
                  <span className="font-bold text-[#0F172A] tabular-nums">{p.clicks_from_search}</span>
                </button>
                {openProductId === p.product_id && perProduct[p.product_id] && (
                  <ListingDetail
                    detail={perProduct[p.product_id]}
                    onBoost={() => setBoostFor(p.product_id)}
                  />
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
      {boostFor && (
        <BoostModal
          productId={boostFor}
          onClose={() => setBoostFor(null)}
        />
      )}
    </div>
  );
}

function ListingDetail({ detail, onBoost }) {
  const lowCtr = detail.overall_ctr_pct < 5 && detail.total_impressions >= 10;
  return (
    <div className="pl-3 pb-3 pt-2 space-y-2">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
        <span>per-query CTR</span>
        <span className={`px-2 py-0.5 rounded-full font-bold ${lowCtr ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
          overall {detail.overall_ctr_pct}%
        </span>
      </div>
      {detail.queries.length === 0 ? (
        <div className="text-xs text-[#94A3B8]">No queries clicked yet.</div>
      ) : (
        <ul className="space-y-1">
          {detail.queries.slice(0, 6).map((q, i) => (
            <li key={`pq-${i}`} className="grid grid-cols-[1fr_auto_auto] gap-2 text-xs items-center">
              <span className="text-[#0F172A] truncate">&ldquo;{q.q || "(empty)"}&rdquo;</span>
              <span className="text-[#94A3B8] tabular-nums">{q.clicks}/{q.impressions}</span>
              <span className={`font-bold tabular-nums ${q.ctr_pct < 5 ? "text-amber-700" : "text-emerald-700"}`}>{q.ctr_pct}%</span>
            </li>
          ))}
        </ul>
      )}
      {lowCtr && (
        <button
          data-testid={`boost-${detail.product_id}`}
          onClick={onBoost}
          className="w-full mt-2 text-xs flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-[#F59E0B] to-[#DD2A7B] text-white font-bold hover:brightness-110 transition"
        >
          <Zap size={12} /> Boost this listing — AI title rewrite
        </button>
      )}
    </div>
  );
}

function BoostModal({ productId, onClose }) {
  const [busy, setBusy] = useState(true);
  const [data, setData] = useState(null);
  const [picking, setPicking] = useState(null);

  useEffect(() => {
    let alive = true;
    api.post(`/products/${productId}/boost-rewrite`)
      .then((r) => alive && setData(r.data))
      .catch((e) => { if (alive) { toast.error(e?.response?.data?.detail || "Rewrite failed"); onClose(); } })
      .finally(() => alive && setBusy(false));
    return () => { alive = false; };
  }, [productId, onClose]);

  const apply = async (candidate) => {
    setPicking(candidate.title);
    try {
      await api.patch(`/products/${productId}`, { title: candidate.title });
      toast.success("Title updated — buyers will see the new copy on next search.");
    } catch (e) {
      // Fallback to clipboard if PATCH fails
      try {
        await navigator.clipboard?.writeText(candidate.title);
        toast.error("Update failed — title copied to clipboard so you can paste it manually.");
      } catch {
        toast.error(e?.response?.data?.detail || "Update failed");
      }
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="boost-modal"
    >
      <div className="glass-heavy rounded-3xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#F59E0B] mb-1">AI rewrite</div>
            <h3 className="font-display text-xl font-bold">Boost this listing</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <X size={16} />
          </button>
        </div>
        {busy ? (
          <div className="text-sm text-[#64748B] py-8 text-center">Generating with Claude…</div>
        ) : !data ? null : (
          <>
            <div className="text-xs text-[#94A3B8] mb-1">Current title</div>
            <div className="text-sm font-bold text-[#0F172A] mb-3">{data.current_title}</div>
            <div className="text-xs text-[#94A3B8] mb-2">Buyer queries that converted: <span className="text-[#0F172A]">{data.query_hint}</span></div>
            <div className="space-y-2 mt-3">
              {data.candidates.map((c, i) => (
                <button
                  key={`cand-${i}`}
                  onClick={() => apply(c)}
                  data-testid={`boost-candidate-${i}`}
                  disabled={!!picking}
                  className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-[#DD2A7B] hover:bg-pink-50/50 transition disabled:opacity-50"
                >
                  <div className="font-bold text-sm text-[#0F172A]">{c.title}</div>
                  <div className="text-xs text-[#64748B] mt-1">{c.why}</div>
                </button>
              ))}
            </div>
            <div className="text-[10px] text-[#94A3B8] mt-3 text-center">Tap a candidate to apply it instantly to your listing.</div>
          </>
        )}
      </div>
    </div>
  );
}



function BoostHistoryCard({ entries }) {
  return (
    <div className="glass-heavy rounded-2xl p-5" data-testid="boost-history-card">
      <div className="flex items-center gap-2 mb-3">
        <Zap size={16} className="text-[#F59E0B]" />
        <div className="font-display font-bold">Boost history</div>
        <div className="text-xs text-[#94A3B8]">title rewrites you applied · CTR before vs. after</div>
      </div>
      <ul className="space-y-2.5">
        {entries.slice(0, 10).map((e, i) => {
          const up = e.ctr_delta_pct > 0;
          const down = e.ctr_delta_pct < 0;
          return (
            <li
              key={`boost-${i}`}
              data-testid={`boost-history-${i}`}
              className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-center border-b border-slate-100 pb-2.5 last:border-0"
            >
              <div className="min-w-0">
                <div className="text-xs text-[#94A3B8] line-through truncate">{e.old_title || "(no old title)"}</div>
                <div className="text-sm font-bold text-[#0F172A] truncate">{e.new_title}</div>
                <div className="text-[10px] text-[#94A3B8] mt-0.5">
                  Applied {new Date(e.applied_at).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <div className="text-right">
                  <div className="text-[#94A3B8]">before</div>
                  <div className="font-bold text-[#0F172A] tabular-nums">{e.before.ctr_pct}%</div>
                </div>
                <div className="text-[#94A3B8]">→</div>
                <div className="text-right">
                  <div className="text-[#94A3B8]">after</div>
                  <div className="font-bold text-[#0F172A] tabular-nums">{e.after.ctr_pct}%</div>
                </div>
                <div
                  className={`px-2 py-0.5 rounded-full font-bold tabular-nums ${
                    up ? "bg-emerald-100 text-emerald-800" : down ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {up ? "+" : ""}{e.ctr_delta_pct}%
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}



/**
 * AI Ops console for creators — ask Claude things like "why did engagement
 * drop last week?" with a compact slice of the live analytics JSON injected as
 * grounding context. Streams via the same /ai/chat/stream SSE endpoint used by
 * the assistant dock and Admin Health page.
 */
function CreatorAIOpsConsole({ data, searchStats, boostHistory, days }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const ask = async () => {
    if (!input.trim() || busy) return;
    const q = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setBusy(true);

    // Compact context — top rows, totals, search summary, recent boosts. Keep it tight.
    const topRows = (data?.rows || []).slice(0, 12).map((r) => ({
      content: (r.content || "").slice(0, 100),
      reactions: r.reactions,
      engagement_per_hour: r.engagement_per_hour,
      revenue_net: r.revenue_net,
      hours_live: r.hours_live,
      zone: r.zone,
      media_type: r.media_type,
    }));
    const context = {
      window_days: days,
      totals: data?.totals,
      top_posts: topRows,
      top_queries: (searchStats?.top_queries || []).slice(0, 8),
      product_clicks: (searchStats?.products || []).slice(0, 6).map((p) => ({
        title: p.title, clicks_from_search: p.clicks_from_search,
      })),
      recent_boosts: (boostHistory || []).slice(0, 4).map((b) => ({
        old_title: (b.old_title || "").slice(0, 60),
        new_title: (b.new_title || "").slice(0, 60),
        ctr_delta_pct: b.ctr_delta_pct,
      })),
    };
    const system = `You are NEXUS Growth Analyst — a candid creator-side data coach. The creator just asked you a question. Use ONLY the JSON snapshot below to answer. Be concrete and actionable: 1-3 bullets max, plus one recommended next action. No fluff. JSON snapshot:\n${JSON.stringify(context)}`;

    try {
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: q, provider: "anthropic", system }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Analyst stream failed");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const block of parts) {
          let evt = "message"; let payload = "";
          for (const line of block.split("\n")) {
            if (line.startsWith("event:")) evt = line.slice(6).trim();
            else if (line.startsWith("data:")) payload += line.slice(5).trim();
          }
          if (!payload) continue;
          try {
            const parsed = JSON.parse(payload);
            if (evt === "delta" && parsed.content) {
              setMessages((m) => {
                const arr = [...m];
                arr[arr.length - 1] = { role: "assistant", text: arr[arr.length - 1].text + parsed.content };
                return arr;
              });
            }
          } catch { /* ignore parse errors mid-stream */ }
        }
      }
    } catch (e) {
      toast.error(e.message || "Analyst unreachable");
    } finally {
      setBusy(false);
    }
  };

  const suggestions = [
    "Why did engagement drop last week?",
    "Which post format is winning for me right now?",
    "Which listing should I AI-boost first?",
  ];

  return (
    <div className="glass-heavy rounded-2xl p-5" data-testid="creator-ai-ops-console">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="creator-ai-ops-toggle"
        className="w-full flex items-center gap-2 mb-3 group"
      >
        <Bot size={16} className="text-[#DD2A7B]" />
        <div className="font-display font-bold text-left">Ask the analyst</div>
        <div className="text-xs text-[#64748B] ml-2 hidden sm:block">Claude reads your live numbers and tells you what to do</div>
        <div className="ml-auto text-xs text-[#DD2A7B] group-hover:underline">{open ? "Hide" : "Open"}</div>
      </button>

      {open && (
        <>
          <div
            ref={scrollRef}
            data-testid="creator-ai-ops-log"
            className="max-h-72 overflow-y-auto rounded-xl bg-slate-50 p-3 space-y-2.5 mb-3"
          >
            {messages.length === 0 ? (
              <div className="text-xs text-[#64748B]">
                <div className="mb-2">Ask anything about your last {days} days. Try:</div>
                <ul className="space-y-1">
                  {suggestions.map((s) => (
                    <li key={s}>
                      <button
                        data-testid="creator-ai-ops-suggest"
                        onClick={() => setInput(s)}
                        className="text-left text-[#DD2A7B] hover:underline"
                      >
                        → {s}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={`msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[88%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#DD2A7B] text-white"
                        : "bg-white border border-slate-200 text-[#0F172A]"
                    }`}
                  >
                    {m.text || (busy && i === messages.length - 1 ? <span className="text-[#94A3B8]">…</span> : "")}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="creator-ai-ops-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder="Why did engagement drop?"
              className="nexus-input"
            />
            <button
              data-testid="creator-ai-ops-send"
              onClick={ask}
              disabled={busy || !input.trim()}
              className="nexus-btn-primary disabled:opacity-50 !py-2.5 !px-4"
              aria-label="Ask"
            >
              <Send size={14} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
